// Contatos de uma tag (mockup "Modal Contatos da Tag A"): chip + total, XLSX
// (no cliente), filtro local, lista com avatar/número/"há X dias" e remover
// vínculo; rodapé leva para /contacts?tag=<id>.
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from "xlsx";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { avatarColorFor, initialsOf, formatPhone } from "../../pages/Contacts/contactRules";
import { tagChip, sinceLabel, tagContactRow } from "../../pages/Tags/tagRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    paper: { width: 520, maxWidth: "calc(100vw - 32px)", margin: 16, maxHeight: "calc(var(--vh-full) - 32px)" },
    header: { gap: 10 },
    chip: { display: "inline-flex", alignItems: "center", fontSize: 12, fontWeight: 600, borderRadius: 6, padding: "4px 12px", whiteSpace: "nowrap" },
    total: { fontSize: 15, fontWeight: 700, color: theme.palette.text.primary, fontFamily: AEROS.fontDisplay },
    headerActions: { marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" },
    xlsx: { border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)"}`, borderRadius: 9, padding: "4px 10px", fontSize: 12, fontWeight: 600, color: theme.palette.text.primary, "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 } },
    search: {
      margin: "12px 24px 0",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      padding: "4px 12px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 17, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
    },
    list: { padding: "10px 24px 4px", maxHeight: 300, overflowY: "auto", ...theme.scrollbarStyles },
    row: { display: "flex", alignItems: "center", gap: 11, padding: "9px 0", borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}` },
    name: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    number: { fontSize: 11.5, color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums" },
    since: { fontSize: 11, color: theme.palette.aeros.textTertiary, whiteSpace: "nowrap" },
    remove: {
      width: 26, height: 26, borderRadius: 7, padding: 0,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 14 },
      "&:hover": { color: stateText(theme, AEROS.error, "error"), borderColor: alpha(AEROS.error, 0.4), backgroundColor: "transparent" },
    },
    footer: { padding: "12px 24px 16px", display: "flex", alignItems: "center", fontSize: 12, color: theme.palette.text.secondary },
    link: { marginLeft: "auto", fontSize: 12, fontWeight: 600, color: stateText(theme, AEROS.cyan, "info"), background: "none", border: "none", cursor: "pointer", font: "inherit", fontWeight: 600, "&:hover": { textDecoration: "underline" } },
    empty: { padding: "24px 0", textAlign: "center", fontSize: 12.5, color: theme.palette.aeros.textTertiary },
  };
});

const TagContactsModal = ({ open, tag, onClose, onChanged }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const history = useHistory();
  const [contacts, setContacts] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !tag?.id) return;
    setSearch("");
    setLoading(true);
    api
      .get(`/tags/${tag.id}/contacts`, { params: { limit: 200 } })
      .then(({ data }) => {
        setContacts(data.contacts || []);
        setCount(data.count || 0);
      })
      .catch((err) => toastError(err))
      .finally(() => setLoading(false));
  }, [open, tag?.id]);

  const shown = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return contacts;
    return contacts.filter((c) => String(c.name).toLowerCase().includes(t) || String(c.number).includes(t));
  }, [contacts, search]);

  const removeTag = async (contact) => {
    try {
      await api.delete(`/tags-contacts/${tag.id}/${contact.id}`);
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
      setCount((n) => Math.max(0, n - 1));
      toast.success(i18n.t("tagContacts.removed", { name: contact.name }));
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    }
  };

  const exportXlsx = () => {
    const wb = xlsxUtils.book_new();
    xlsxUtils.book_append_sheet(wb, xlsxUtils.json_to_sheet(contacts.map(tagContactRow)), "Contatos");
    xlsxWriteFile(wb, `contatos_${String(tag.name).replace(/[^\w-]+/g, "_")}.xlsx`);
  };

  if (!tag) return null;
  const chip = tagChip(tag);

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }} scroll="paper">
      <div className={m.form}>
        <div className={`${m.header} ${classes.header}`}>
          <span className={classes.chip} style={{ color: chip.color, backgroundColor: chip.bg }}>{tag.name}</span>
          <div className={classes.total}>{i18n.t("tagContacts.total", { count })}</div>
          <div className={classes.headerActions}>
            <Button className={classes.xlsx} startIcon={<GetAppIcon />} onClick={exportXlsx} disabled={contacts.length === 0}>
              XLSX
            </Button>
            <IconButton className={m.closeButton} style={{ marginLeft: 0 }} onClick={onClose} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <InputBase
          className={classes.search}
          placeholder={i18n.t("tagContacts.filter")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          startAdornment={<SearchIcon />}
          inputProps={{ "aria-label": i18n.t("tagContacts.filter") }}
        />

        <div className={classes.list}>
          {loading && (
            <div className={classes.empty}>
              <CircularProgress size={20} />
            </div>
          )}
          {!loading && shown.length === 0 && <div className={classes.empty}>{i18n.t("tagContacts.empty")}</div>}
          {shown.map((c) => (
            <div key={c.id} className={classes.row}>
              <div className={m.avatar} style={{ width: 32, height: 32, fontSize: 11, backgroundColor: avatarColorFor(c.id) }}>
                {c.urlPicture && !/nopicture\.png$/i.test(c.urlPicture) ? <img src={c.urlPicture} alt={c.name} /> : initialsOf(c.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={classes.name}>{c.name}</div>
                <div className={classes.number}>{c.isGroup ? c.number : formatPhone(c.number)}</div>
              </div>
              <span className={classes.since}>{sinceLabel(c.taggedAt)}</span>
              <Tooltip title={i18n.t("tagContacts.remove")}>
                <IconButton className={classes.remove} onClick={() => removeTag(c)} aria-label={i18n.t("tagContacts.remove")}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>

        <div className={classes.footer}>
          {i18n.t("tagContacts.showing", { shown: shown.length, total: count })}
          <button
            type="button"
            className={classes.link}
            onClick={() => {
              onClose();
              history.push(`/contacts?tag=${tag.id}`);
            }}
          >
            {i18n.t("tagContacts.seeAll")}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default TagContactsModal;
